import FormikDateInput from '@perpay-web/components/base/DateInput/FormikDateInput';
import { useModal } from '@perpay-web/components/base/Modal/useModal';
import FormikMoneyInput from '@perpay-web/components/base/MoneyInput/FormikMoneyInput';
import FormikSelectInput from '@perpay-web/components/base/SelectInput/FormikSelectInput';
import FormikSsnInput from '@perpay-web/fintech/components/base/SsnInput/FormikSsnInput';
import EmployerInfoModal from '@perpay-web/fintech/components/composite/EmployerInfoModal/EmployerInfoModal';
import CompanyPickerServerError from '@perpay-web/fintech/components/higher-order/WithFieldServerErrors/CompanyPickerServerError';
import { payCycleOptions } from '@perpay-web/fintech/constants/payCycle';
import { useFocusLayoutBodyLockScrollFix } from '@perpay-web/hooks/useFocusLayoutBodyLockScrollFix';
import { useIsMobile } from '@perpay-web/hooks/useIsMobile';
import { EndToEndTestIdProvider } from '@perpay-web/utils/endToEndTestIds';
import { noop } from '@perpay-web/utils/noop';

import styles from './CardApplicationEmploymentDetailsForm.scss';

// @ts-check

/**
 *
 * @typedef {Object} SelfOnboardingData
 * @property {string} [companyName]
 *
 * @typedef {Object} EmployerSelfOnboardResult
 * @property {string} uuid
 * @property {string} name
 * @property {string} displayName
 * @property {SelfOnboardingData} selfOnboardingData
 *
 * @typedef {Object} CardApplicationEmploymentDetailsFormProps
 * @property {import('formik').FormikProps<any>} formik
 * @property {boolean=} isSSIExemption
 * @property {Record<string, unknown>} [errors]
 * @property {boolean=} isUserIdentityVerified
 * @property {boolean=} isMarketplaceSignup
 * @property {() => void} analyticsOnViewEmployerInfoModal
 * @property {(data: SelfOnboardingData) => void} setSelfOnboardingData
 * @property {(value: any) => void=} onCompanySelect
 * @property {() => void=} onSSIModalMount
 * @property {() => void=} onSSIModalChangeEmployer
 * @property {() => void=} onSSIModalContinue
 */

/**
 * @param {CardApplicationEmploymentDetailsFormProps} props
 */
const CardApplicationEmploymentDetailsForm = (props) => {
    const {
        isSSIExemption = false,
        formik,
        errors = {},
        isUserIdentityVerified = false,
        isMarketplaceSignup = false,
        analyticsOnViewEmployerInfoModal,
        setSelfOnboardingData,
        onCompanySelect = noop,
        onSSIModalMount,
        onSSIModalChangeEmployer,
        onSSIModalContinue,
    } = props;

    const { isMobile } = useIsMobile();
    const {
        isModalOpen: isEmployerModalOpen,
        setIsModalOpen: setEmployerModalOpen,
    } = useModal(false);
    useFocusLayoutBodyLockScrollFix(isEmployerModalOpen);

    return (
        <>
            {!isUserIdentityVerified ? (
                <div className={styles['flex-row']}>
                    <div className={styles.input__container}>
                        <EndToEndTestIdProvider testId='dateOfBirth'>
                            <FormikDateInput
                                name='dateOfBirth'
                                label='Date of Birth (MM/DD/YYYY)'
                            />
                        </EndToEndTestIdProvider>
                    </div>
                    <div className={styles.input__container}>
                        <EndToEndTestIdProvider testId='ssn'>
                            <FormikSsnInput
                                name='ssn'
                                placeholder='xxx-xx-xxxx'
                                numbersOnly
                                errors={errors}
                            />
                        </EndToEndTestIdProvider>
                        <div className={styles.note}>
                            Entering your SSN will not impact your credit score.
                        </div>
                    </div>
                </div>
            ) : null}
            <div className={styles['flex-row']}>
                <div className={styles.input__container}>
                    <CompanyPickerServerError
                        isSSIExemption={isSSIExemption}
                        name='selectedCompany'
                        label='Employer'
                        initialValue={formik.values.selectedCompany.company}
                        isMarketplaceSignup={isMarketplaceSignup}
                        onInputValueChange={() => {
                            formik.setFieldValue('selectedCompany', {
                                company: '',
                                uuid: '',
                            });
                            setSelfOnboardingData({});
                        }}
                        onSelect={async (value) => {
                            await formik.setFieldValue(
                                'selectedCompany',
                                value,
                            );
                            formik.setFieldTouched(
                                'selectedCompany',
                                true,
                                true,
                            );
                            onCompanySelect(value);
                        }}
                        onSubmitSelfOnboardEmployer={
                            /**
                             * @param {EmployerSelfOnboardResult} value
                             */
                            async (value) => {
                                await formik.setFieldValue('selectedCompany', {
                                    uuid: value.uuid,
                                });
                                formik.setFieldTouched(
                                    'selectedCompany',
                                    true,
                                    true,
                                );
                                setSelfOnboardingData(value.selfOnboardingData);
                            }
                        }
                        placeholder='Start typing your Employer'
                        onSSIModalMount={onSSIModalMount}
                        onSSIModalChangeEmployer={onSSIModalChangeEmployer}
                        onSSIModalContinue={onSSIModalContinue}
                    />
                    <div className={styles.note}>
                        {isMobile ? 'We will' : "We'll"} never contact your
                        employer.{' '}
                        <div
                            onClick={() => setEmployerModalOpen(true)}
                            className={styles.modal}
                            role='button'
                            tabIndex={0}
                            onKeyDown={(e) => {
                                if (e.key === 'Enter' || e.key === ' ')
                                    setEmployerModalOpen(true);
                            }}
                        >
                            Why do we ask this?{' '}
                            <div className={styles.info__icon} />
                        </div>{' '}
                    </div>
                </div>
                <div className={styles.input__container}>
                    <EndToEndTestIdProvider testId='payCycle'>
                        <FormikSelectInput
                            label='Pay cycle'
                            name='payCycle'
                            includeEmptyOption
                            options={payCycleOptions}
                        />
                    </EndToEndTestIdProvider>
                </div>
            </div>
            <div className={styles['flex-row']}>
                <div className={styles.input__container}>
                    <EndToEndTestIdProvider testId='estimatedNetPay'>
                        <FormikMoneyInput
                            name='estimatedNetPay'
                            label='Paycheck amount'
                            numbersOnly
                        />
                    </EndToEndTestIdProvider>
                    <div className={styles.note}>
                        This is how much you take home each paycheck after tax.
                    </div>
                </div>
            </div>
            {isEmployerModalOpen ? (
                <EmployerInfoModal
                    onClose={() => {
                        analyticsOnViewEmployerInfoModal();
                        setEmployerModalOpen(false);
                    }}
                    onSubmit={() => {
                        analyticsOnViewEmployerInfoModal();
                        setEmployerModalOpen(false);
                    }}
                    product={isMarketplaceSignup ? 'marketplace' : 'card'}
                />
            ) : null}
        </>
    );
};

export default CardApplicationEmploymentDetailsForm;
